import React, { useState, useEffect } from "react";
import apiClient from "@/api/client";
import { Button } from "@/components/ui/button";
import { FileText, FileSpreadsheet, Mail, AlertTriangle, Clock, XCircle, Building2 } from "lucide-react";
import { formatDateEU } from "@/utils/dateFormat";
import { useAuth } from "@/contexts/AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ReportSection = ({ title, icon: Icon, color, items }) => (
  <div className="crm-card mb-4">
    <div className="flex items-center gap-2 mb-3">
      <Icon size={18} className={`text-${color}-500`} />
      <h3 className="crm-card-title mb-0">{title}</h3>
      <span className={`crm-badge bg-${color}-100 text-${color}-700`}>{items.length}</span>
    </div>
    {items.length === 0 ? (
      <p className="text-sm text-[#577083]">Aucun</p>
    ) : (
      <div className="crm-table-wrap" style={{boxShadow:'none'}}>
        <table className="crm-table">
          <thead><tr><th>Nom</th><th>Abonnement</th><th>Paiement</th><th>Échéance</th><th>Jours</th></tr></thead>
          <tbody>
            {items.map((s) => (
              <tr key={s.id}>
                <td className="font-medium">{s.member_name}</td>
                <td>{s.plan_name}</td>
                <td>{s.payment_type}</td>
                <td>{formatDateEU(s.end_date_effective || s.end_date)}</td>
                <td className="font-bold">{s.days_remaining}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

const ReportsPage = () => {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [emailSent, setEmailSent] = useState(false);
  const { isAdmin } = useAuth();

  useEffect(() => {
    apiClient.get("/reports/daily").then((r) => setReport(r.data)).catch(console.error).finally(() => setLoading(false));
  }, []);

  const handleExportCSV = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/reports/export/csv`, { credentials: "include" });
      if (!res.ok) throw new Error("Export échoué");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = "rapport_bhealth.csv";
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) { alert("Erreur d'export CSV: " + err.message); }
  };

  const handleExportPDF = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/reports/export/pdf`, { credentials: "include" });
      if (!res.ok) throw new Error("Export échoué");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = "rapport_bhealth.pdf";
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) { alert("Erreur d'export PDF: " + err.message); }
  };

  const handleSendEmail = () => {
    setEmailSent(true);
    setTimeout(() => setEmailSent(false), 3000);
  };

  if (loading) return <div className="crm-page-loading">Chargement du rapport...</div>;

  return (
    <div className="crm-page">
      <div className="crm-page-header">
        <div>
          <h1 className="crm-page-title">Rapport quotidien</h1>
          <p className="crm-page-subtitle">Généré le {formatDateEU(report?.date) || formatDateEU(new Date().toISOString())}</p>
        </div>
        <div className="flex gap-2">
          {isAdmin && (
            <>
              <Button variant="outline" onClick={handleExportCSV} data-testid="export-csv-btn">
                <FileSpreadsheet size={16} className="mr-1" /> Export CSV
              </Button>
              <Button variant="outline" onClick={handleExportPDF} data-testid="export-pdf-btn">
                <FileText size={16} className="mr-1" /> Export PDF
              </Button>
              <Button onClick={handleSendEmail} className="bg-[#1a2744] hover:bg-[#243459] text-[#e8d44d]" data-testid="send-email-btn">
                <Mail size={16} className="mr-1" /> {emailSent ? "Envoyé ! (démo)" : "Envoyer par email"}
              </Button>
            </>
          )}
        </div>
      </div>

      {report && (
        <>
          <ReportSection title="J-15 — ROUGE" icon={AlertTriangle} color="red" items={report.j15} />
          <ReportSection title="J-30 — ORANGE" icon={Clock} color="orange" items={report.j30} />
          <ReportSection title="Expirés" icon={XCircle} color="gray" items={report.expired} />
          <ReportSection title="Domiciliation (échéance dans le mois)" icon={Building2} color="yellow" items={report.domiciliation_month} />
        </>
      )}
    </div>
  );
};

export default ReportsPage;
